import useSWR, { mutate } from "swr";
import { useFetcher } from "~/hooks/useFetcher";
import {
  type AccessLevelOption,
  type ShareCollaborators,
  type ShareGroup,
  type ShareUser,
} from "~/pages/manifold-nextgen/components/share/types";
import { groupPrincipal, isEveryoneId, parsePrincipal, userPrincipal } from "~/services/principals";
import {
  ProjectAccessLevel,
  ProjectCollaborator,
  ProjectCollaboratorList,
  ProjectCollaboratorMember,
  fetchProjectCollaboratorsBatch,
  projectSharesBatchPath,
  projectSharesPath,
  shareProject,
  unshareProject,
} from "~/services/projects";

// Label shown for the `user:*` wildcard collaborator in place of a person's name.
const EVERYONE_LABEL = "All users";

// Batch SWR keys are `[batchPath, idsKey]` tuples; match any of them so a share
// change refreshes the ProjectCard avatars regardless of which page's ids they
// were fetched for.
const BATCH_SHARES_PATH = projectSharesBatchPath();
const isProjectCollaboratorsBatchKey = (key: unknown): boolean =>
  Array.isArray(key) && key[0] === BATCH_SHARES_PATH;

const accessLevels: Record<string, AccessLevelOption> = {
  [ProjectAccessLevel.OWNER]: {
    label: "Owner",
    description: "Full access, including managing agents, datasets, and sharing",
  },
  [ProjectAccessLevel.USER]: {
    label: "User",
    description: "Can use the project and its agents",
  },
};

const collaboratorFullName = (collaborator: ProjectCollaborator, id: string): string =>
  [collaborator.given_name, collaborator.family_name].filter(Boolean).join(" ") || id;

const memberFullName = (member: ProjectCollaboratorMember): string =>
  [member.given_name, member.family_name].filter(Boolean).join(" ") || member.email;

// The backend returns a flat list of collaborators; ShareModal wants users and
// groups split out, each carrying its access level as a plain string. Group
// collaborators carry the resolved display name and (on the single-project
// endpoint) their expanded members, who inherit the group's access level.
export const toCollaborators = (collaborators: ProjectCollaborator[]): ShareCollaborators => {
  const users: ShareUser[] = [];
  const groups: ShareGroup[] = [];
  for (const collaborator of collaborators) {
    const { isGroup, id } = parsePrincipal(collaborator.principal);
    if (isGroup) {
      groups.push({
        name: id,
        display_name: collaborator.display_name ?? id,
        is_internal: collaborator.is_internal ?? false,
        access_level: collaborator.level,
        users: (collaborator.members ?? []).map((member) => ({
          email: member.email,
          full_name: memberFullName(member),
          image_url: member.image_url ?? undefined,
          access_level: collaborator.level,
        })),
      });
    } else {
      users.push({
        email: id,
        full_name: isEveryoneId(id) ? EVERYONE_LABEL : collaboratorFullName(collaborator, id),
        image_url: collaborator.image_url ?? undefined,
        access_level: collaborator.level,
      });
    }
  }
  return { users, groups };
};

export const useProjectShare = (projectId: string) => {
  const {
    data: collaboratorList,
    isLoading,
    error: collaboratorsError,
  } = useFetcher<ProjectCollaboratorList>(projectSharesPath(projectId));

  const collaborators = collaboratorList ? toCollaborators(collaboratorList.items) : undefined;

  // A principal holds at most one level here, so a level change is a share at the
  // new level followed by an unshare of the old; both endpoints are idempotent.
  // Share first so the principal is never transiently left without access; a
  // principal who momentarily holds both levels is reported at the higher one.
  const handleShare = async (
    recipient: { user?: ShareUser; group?: ShareGroup },
    accessLevel: string | null
  ) => {
    const { user, group } = recipient;
    const principal = user ? userPrincipal(user.email) : group ? groupPrincipal(group.name) : null;
    if (!principal) {
      return;
    }

    const currentLevel =
      (user
        ? collaborators?.users.find((u) => u.email.toLowerCase() === user.email.toLowerCase())
        : collaborators?.groups.find((g) => g.name === group?.name)
      )?.access_level ?? null;

    if (accessLevel === null) {
      if (currentLevel) {
        await unshareProject(projectId, principal, currentLevel as ProjectAccessLevel);
      }
    } else {
      await shareProject(projectId, principal, accessLevel as ProjectAccessLevel);
      if (currentLevel && currentLevel !== accessLevel) {
        await unshareProject(projectId, principal, currentLevel as ProjectAccessLevel);
      }
    }
    // Refresh this project's collaborators (settings) and the batch-fetched ones
    // backing the ProjectCard avatars, both now stale.
    await mutate(projectSharesPath(projectId));
    await mutate(isProjectCollaboratorsBatchKey);
  };

  return {
    collaborators,
    isLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel: ProjectAccessLevel.USER as string,
  };
};

// Second-phase loader for a page of project cards: returns a `{ id -> collaborators }`
// lookup once resolved, or `undefined` while loading. Keyed on the sorted ids so
// reordering or client-side filtering doesn't trigger a refetch. A failure here
// leaves avatars empty without disturbing the already-rendered list.
export const useProjectCollaboratorsBatch = (
  projectIds: string[]
): Record<string, ProjectCollaborator[]> | undefined => {
  const idsKey = [...projectIds].sort().join(",");
  const { data, error } = useSWR(projectIds.length ? [BATCH_SHARES_PATH, idsKey] : null, () =>
    fetchProjectCollaboratorsBatch(projectIds)
  );
  // Resolve to an empty lookup on failure so cards drop the skeleton and render
  // without avatars, rather than pulsing forever waiting on data that won't come.
  if (error) {
    return {};
  }
  return data?.collaborators_by_project;
};
